import { assertReportAdmission, NativeConditionError } from "./nativeConditions.mjs";
import { assertTypedData, createTypedExpressionSession, NativeExpressionError } from "./nativeExpression.mjs";
import { compileTypedReportTargets } from "./nativeTypedReport.mjs";
import { resolveReportParameterValues } from "./nativeParameters.mjs";

const permits = new WeakMap();
const executions = new WeakMap();
function fail(message) { throw new NativeConditionError("UNSUPPORTED_REPORT_PROFILE", "$.typedExecution", message); }
function snapshot(payload) {
  let nodes = 0;
  const active = new WeakSet();
  function visit(value, depth) {
    if (++nodes > 200000 || depth > 64) fail("Internal typed source exceeds the bounded declarative envelope.");
    if (value === null || value === undefined || ["string", "boolean"].includes(typeof value)) return;
    if (typeof value === "number" && Number.isFinite(value)) return;
    if (typeof value !== "object" || active.has(value)) fail("Internal typed sources must be finite, acyclic declarative data.");
    assertTypedData(value, Array.isArray(value) ? 200000 : undefined);
    active.add(value);
    Object.values(value).forEach(child => visit(child, depth + 1));
    active.delete(value);
  }
  visit(payload, 0);
  return JSON.stringify(payload);
}
function compile(payload) {
  const admission = assertReportAdmission(payload, "validate");
  if (admission.requiredFeatures.some(feature => !["typed-expressions-v1", "report-parameters-v1", "conditional-rules-v1"].includes(feature))) fail("Typed internal execution does not admit unknown features.");
  const tables = [], texts = [];
  for (const band of payload.layout.bands) for (const element of band.elements) {
    if (element.type === "table" && (element.columns.some(column => column.expr?.typed !== undefined) || payload.layout.presentationFilters?.some(filter => filter.tableId === element.id))) tables.push(element);
    if (element.type === "text" && element.expr?.typed !== undefined) texts.push({ element, band });
  }
  return compileTypedReportTargets(payload, tables, texts);
}
export function createInternalTypedExecution(payload, inputs) {
  if (process.env.NODE_ENV === "production") fail("Internal typed execution is unavailable in production.");
  const original = snapshot(payload);
  const { parameters } = compile(payload);
  const values = resolveReportParameterValues(parameters, inputs);
  const permit = Object.freeze({});
  permits.set(permit, { payload, snapshot: original, values });
  return permit;
}
export function prepareNativeTypedExecution(payload, permit) {
  if (permit === undefined) return payload;
  const owner = permits.get(permit);
  if (owner?.payload !== payload || owner.snapshot !== snapshot(payload)) fail("Invalid, stale or mismatched internal typed execution.");
  const { tablePlans, textPlans, filterPlans } = compile(payload);
  const prepared = { ...payload };
  const session = createTypedExpressionSession();
  const document = Object.create(null);
  for (const dataset of payload.data.datasets) if (dataset.kind === "single") document[dataset.id] = payload.datasets[dataset.id];
  const tableCache = new Map();
  const textCache = new Map();
  function guarded(action) {
    try { return action(); }
    catch (error) {
      if (!(error instanceof NativeExpressionError)) throw error;
      throw new NativeConditionError(error.code, error.path, error.message);
    }
  }
  executions.set(prepared, {
    source: payload,
    hasTable: table => tablePlans.has(table.id),
    table(table, row) {
      if (!tableCache.has(table)) tableCache.set(table, new WeakMap());
      const cache = tableCache.get(table);
      if (!cache.has(row)) cache.set(row, guarded(() => tablePlans.get(table.id).evaluate({ detail: row, document, parameters: owner.values }, session)));
      return cache.get(row);
    },
    text(element, row) {
      if (!textCache.has(element)) textCache.set(element, new Map());
      const cache = textCache.get(element);
      if (!cache.has(row)) cache.set(row, guarded(() => session.evaluate(textPlans.get(element.id), { document, parameters: owner.values, ...(row !== undefined ? { detail: row } : {}) })));
      return cache.get(row);
    },
    retain(table, row, calculated) {
      return (filterPlans.get(table.id) || []).every(plan => guarded(() => session.evaluate(plan, { detail: row, document, parameters: owner.values }, calculated)) === true);
    },
    get work() { return session.work; },
  });
  return prepared;
}
export function nativeTypedExecution(payload) { return executions.get(payload); }
export function inheritNativeTypedExecution(source, next) {
  const execution = executions.get(source);
  if (execution) executions.set(next, execution);
}
export function internalTypedInputs(payload, permit) {
  const owner = permits.get(permit);
  if (owner?.payload !== payload || owner.snapshot !== snapshot(payload)) fail("Invalid, stale or mismatched internal typed execution.");
  return owner.values;
}
